import React, { Component, type ReactNode } from 'react'
import { SVGIcon, Button, LoadingBox, colors } from 'ic-snacks'
import { type InjectedDashIntlProps } from 'intl/intl.types'
import { injectIntl, FormattedMessage } from 'react-intl'
import range from 'lodash/range'
import debounce from 'lodash/debounce'
import styled from '@emotion/styled'
import FilterBar from 'legacy/components/FilterBar'
import TextField from 'legacy/components/Forms/TextField'
import Dropdown from 'legacy/components/Dropdown'
import Divider from 'legacy/components/Divider'
import withRetailerRouter from 'legacy/common/withRouter'
import { type ScopedRouteParams } from 'legacy/common/types'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { withFlipperContext } from 'legacy/common/context'
import { type FlipperContextValue } from 'legacy/contexts/flipper/FlipperContext'
import EditDepartmentModal from 'legacy/routes/catalog/departments/EditDepartmentModal'
import DepartmentCard from 'legacy/routes/catalog/departments/departmentCard/DepartmentCard'
import { type Department } from 'legacy/routes/catalog/types'
import { withTrackEventOnMount } from 'utils/events/hocs'
import { trackEvent } from 'utils/events/trackEvent'
import AppHeader from 'legacy/components/AppHeader'
import DepartmentsBreadcrumbs from 'legacy/routes/catalog/breadcrumbs/DepartmentsBreadcrumbs'
import { DeprecationBanner } from 'legacy/routes/catalog/departments/utils/deprecationBanner'

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 10px 50px 0 10px;
`

const Filters = styled.div`
  display: flex;
`

const DepartmentsContainer = ({ children }: { children: ReactNode }) => (
  <DepartmentCards data-testid="departments">{children}</DepartmentCards>
)

const DepartmentCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`

interface DepartmentsViewParams extends ScopedRouteParams {}

interface Props extends RetailerRouteComponentProps<DepartmentsViewParams>, FlipperContextValue {
  departments?: Department[]
  onDepartmentDeleted(departmentId: number): void
  onDepartmentsOrderChange(departmentId: number, rankOffsetPosition: number): void
  onReload(): void
}

interface State {
  filterText: string
  filterType?: string
  filterStatus?: string
  newDepartmentModalIsOpen: boolean
  editDepartmentModalId?: number
}

class DepartmentsView extends Component<Props & InjectedDashIntlProps, State> {
  state: State = {
    filterText: '',
    filterType: undefined,
    filterStatus: undefined,
    newDepartmentModalIsOpen: false,
    editDepartmentModalId: undefined,
  }

  debouncedFilterTrackEvent = debounce(
    () =>
      trackEvent({
        id: 'catalog.products.departments.filtered_departments',
        description: 'Filtered the departments',
      }),
    500
  )

  handleClearFilters = () => {
    trackEvent({
      id: 'catalog.products.departments.cleared_filter',
      description: 'Cleared the departments filter',
    })

    this.setState({
      filterText: '',
      filterType: undefined,
      filterStatus: undefined,
    })
  }

  onDepartmentsSort = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    const { departments, onDepartmentsOrderChange } = this.props

    if (!departments) {
      return
    }

    const filteredDepartments = this.getFilteredDepartments()
    const activeDepartments = filteredDepartments.filter(
      department => department.status !== 'inactive'
    )

    const department = activeDepartments[oldIndex]
    const rankOffsetPosition = departments.findIndex(d => d === activeDepartments[newIndex])

    onDepartmentsOrderChange(department.id, rankOffsetPosition)
  }

  getFilteredDepartments(): Department[] {
    const { departments } = this.props
    const { filterType, filterStatus, filterText } = this.state

    // Don't track on initial page load
    if (filterType || filterStatus || filterText) {
      this.debouncedFilterTrackEvent()
    }

    return departments
      ? departments
          .filter(department => !filterStatus || filterStatus === department.status)
          .filter(
            department =>
              !filterType || (filterType === 'custom' ? department.editable : !department.editable)
          )
          .filter(department => !filterText || department.name.match(new RegExp(filterText, 'i')))
      : []
  }

  onEditDepartmentModalClose = () => {
    const { onReload } = this.props
    this.setState({ newDepartmentModalIsOpen: false, editDepartmentModalId: undefined })
    onReload()
  }

  render() {
    const {
      intl,
      onDepartmentDeleted,
      match: { params },
      departments,
    } = this.props
    const {
      filterType,
      filterStatus,
      filterText,
      newDepartmentModalIsOpen: newModalIsOpen,
      editDepartmentModalId: editModalId,
    } = this.state

    const filteredDepartments = this.getFilteredDepartments()
    const activeDepartments = filteredDepartments.filter(
      department => department.status !== 'inactive'
    )
    const inactiveDepartments = filteredDepartments.filter(
      department => department.status === 'inactive'
    )

    const SortableDepartmentCard = ({ value }: { value: Department }) => (
      <DepartmentCard
        key={value.id}
        onEdit={() => this.setState({ editDepartmentModalId: value.id })}
        onDelete={() => onDepartmentDeleted(value.id)}
        department={value}
      />
    )

    return (
      <div style={{ width: '100%' }} data-testid="departments-view-container">
        <AppHeader title={<DepartmentsBreadcrumbs />} />
        <DeprecationBanner />
        <FilterBar padding="0 30px">
          <Filters>
            <TextField
              value={filterText}
              onChange={(e: React.ChangeEvent) =>
                this.setState({ filterText: (e.target as HTMLInputElement).value })
              }
              type="text"
              elementAttributes={{
                placeholder: intl.formatMessage({
                  id: 'catalog.filter.findDepartment',
                }),
                'data-testid': 'departments-filter-name',
              }}
              name="filter-department"
              prefix={<SVGIcon name="search" color={colors.GRAY_46} style={{ height: '18px' }} />}
            />
            <Dropdown
              width="200px"
              defaultable
              defaultText={intl.formatMessage({
                id: 'dropdown.label.allTypes',
              })}
              value={filterType}
              onChange={(value: string | string[]) =>
                this.setState({
                  filterType: value instanceof Array ? undefined : value,
                })
              }
              options={[
                { label: 'Standard', value: 'standard' },
                { label: 'Custom', value: 'custom' },
              ]}
            />
            <Dropdown
              width="200px"
              defaultable
              value={filterStatus}
              defaultText={intl.formatMessage({
                id: 'dropdown.label.allStatuses',
              })}
              onChange={(value: string | string[]) =>
                this.setState({
                  filterStatus: value instanceof Array ? undefined : value,
                })
              }
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Upcoming', value: 'upcoming' },
                { label: 'Ended', value: 'ended' },
                { label: 'Inactive', value: 'inactive' },
              ]}
            />
            <Button
              snacksStyle="flat"
              size="small"
              disabled={filterStatus == null && filterType == null && filterText.length === 0}
              style={{ textTransform: 'uppercase' }}
              onClick={this.handleClearFilters}
            >
              <FormattedMessage id="button.clear" />
            </Button>
          </Filters>
          <Button
            snacksStyle="primary"
            size="small"
            onClick={() => this.setState({ newDepartmentModalIsOpen: true })}
            elementAttributes={{
              'data-cy': 'create-department',
            }}
          >
            <FormattedMessage id="catalog.department.new" />
          </Button>
        </FilterBar>
        {departments ? (
          <Container>
            <DepartmentsContainer>
              {activeDepartments.map(department => (
                <SortableDepartmentCard key={department.id} value={department} />
              ))}
            </DepartmentsContainer>
            {inactiveDepartments.length > 0 && <Divider />}
            {inactiveDepartments.map(department => (
              <DepartmentCard
                key={department.id}
                onEdit={() => this.setState({ editDepartmentModalId: department.id })}
                onDelete={() => onDepartmentDeleted(department.id)}
                department={department}
              />
            ))}
          </Container>
        ) : (
          <Container data-testid="loading">
            {range(15).map(i => (
              <LoadingBox
                key={i}
                style={{ margin: '20px', float: 'left' }}
                shape="square"
                background="light"
                size={240}
              />
            ))}
          </Container>
        )}
        {newModalIsOpen && (
          <EditDepartmentModal {...params} onClose={this.onEditDepartmentModalClose} />
        )}
        {editModalId && (
          <EditDepartmentModal
            {...params}
            id={editModalId}
            department={filteredDepartments.find(d => d.id === editModalId)}
            onClose={this.onEditDepartmentModalClose}
          />
        )}
      </div>
    )
  }
}

export default withTrackEventOnMount({
  id: 'catalog.departments.department.viewed',
  description: 'Viewed a specific department',
})(injectIntl(withRetailerRouter(withFlipperContext(DepartmentsView))))
